import { MorphoBlueAbi } from '@1delta/abis'
import type { MorphoTypeVaultEntry } from '@1delta/data-sdk'
import { GenericTokenList, MulticallRetryFunction } from '../../types'
import { parseRawAmount } from '../../utils/parsing'
import { createMarketUid } from '../../utils'
import { withIdleExposure } from '../exposure'
import type { VaultMarketExposure } from './types'

/**
 * Context for deriving an exposure's `marketUid` — the join key shared with
 * the lending/markets API. The uid mirrors that path:
 * `createMarketUid(chainId, `${protocol}_${MARKETID}`, underlying)`, where
 * `underlying` is the vault's loan asset (its supply side).
 */
export interface MarketUidContext {
  chainId: string
  underlying: string
  /** Market lender prefix, e.g. `MORPHO_BLUE` or `LISTA_DAO`. */
  protocol: string
}

const marketUidFor = (
  ctx: MarketUidContext | undefined,
  marketId: string,
): string | undefined => {
  if (!ctx?.underlying) return undefined
  try {
    return createMarketUid(
      ctx.chainId,
      `${ctx.protocol}_${marketId.slice(2).toUpperCase()}`,
      ctx.underlying,
    )
  } catch {
    return undefined
  }
}

/**
 * Per-market supply rate + share/asset totals, keyed by lower-cased market id.
 * Supply APR is net of the market-level protocol fee (not the vault
 * performance fee). Shared by the Lista (Moolah) and standard MetaMorpho
 * on-chain fetchers — only the rate *source* differs, the allocation math is
 * identical.
 */
export interface MarketRate {
  supplyApr: number
  totalSupplyAssets: bigint
  totalSupplyShares: bigint
  /** Lowercased collateral token backing the market, '' if unknown. */
  collateralAddress: string
  /** Currently-withdrawable assets in the market — `totalSupplyAssets −
   *  totalBorrowAssets`, raw integer. Drives the vault's real withdrawable
   *  liquidity; omit/0 when the borrow side is unknown (degrades to
   *  treating the allocation as fully liquid). */
  liquidity?: bigint
}

/**
 * Read `<core>.position(marketId, vault).supplyShares` for every
 * (vault, market) pair the vaults actually queue. Keyed by
 * `${vaultLower}:${marketIdLower}`. Empty when the core is unknown.
 *
 * The Morpho Blue and Moolah (Lista) cores share the `position` ABI, so this
 * is protocol-agnostic.
 */
export async function fetchVaultSupplyShares(
  chainId: string,
  core: string | undefined,
  entries: MorphoTypeVaultEntry[],
  marketIdsByVault: string[][],
  multicallRetry: MulticallRetryFunction,
): Promise<Map<string, bigint>> {
  const map = new Map<string, bigint>()
  if (!core) return map

  const calls: { address: string; name: string; params: any[] }[] = []
  const keys: string[] = []
  entries.forEach((entry, i) => {
    const vault = entry.vault.toLowerCase()
    for (const id of marketIdsByVault[i]) {
      calls.push({ address: core, name: 'position', params: [id, entry.vault] })
      keys.push(`${vault}:${id}`)
    }
  })
  if (calls.length === 0) return map

  const res = await multicallRetry({
    chain: chainId,
    calls: calls as any,
    abi: calls.map(() => MorphoBlueAbi) as any,
    allowFailure: true,
  })

  res.forEach((r, k) => {
    // `position` returns [supplyShares, borrowShares, collateral]; failed
    // calls surface as undefined under allowFailure.
    const supplyShares = Array.isArray(r) ? r[0] : (r as any)?.supplyShares
    if (supplyShares == null) return
    try {
      map.set(keys[k], BigInt(supplyShares))
    } catch {}
  })
  return map
}

/**
 * Allocation walk: for each market the vault lends into, convert its supply
 * shares to assets and record the exposure. Returns:
 *  - `depositRate`: allocation-weighted market supply APR over *total*
 *    assets (idle balances dilute it), net of the vault performance fee —
 *    matching how MetaMorpho-style `netApy` is derived.
 *  - `exposures`: per-market breakdown, ordered by weight descending.
 *  - `liquidityFormatted`: real withdrawable underlying — idle deposits plus,
 *    from each allocation, at most the market's liquid cash
 *    (`min(allocation, market.liquidity)`), clamped to `totalAssets`. When no
 *    market exposes a `liquidity`, this equals `totalAssets` (the previous
 *    optimistic ceiling).
 */
export function computeVaultAllocation(
  vaultAddress: string,
  decimals: number,
  totalAssetsFormatted: number,
  feePercent: number,
  priceUsd: number,
  marketIds: string[],
  rateMap: Map<string, MarketRate>,
  positionMap: Map<string, bigint>,
  tokenList: GenericTokenList,
  uidCtx?: MarketUidContext,
): {
  depositRate: number
  exposures: VaultMarketExposure[]
  liquidityFormatted: number
} {
  if (totalAssetsFormatted <= 0) {
    return { depositRate: 0, exposures: [], liquidityFormatted: 0 }
  }
  if (marketIds.length === 0) {
    // Fully idle (or unallocated) vault — all assets are withdrawable.
    return {
      depositRate: 0,
      exposures: withIdleExposure([], totalAssetsFormatted, priceUsd),
      liquidityFormatted: totalAssetsFormatted,
    }
  }

  let weighted = 0
  let allocated = 0
  let withdrawable = 0
  const exposures: VaultMarketExposure[] = []
  for (const id of marketIds) {
    const rate = rateMap.get(id)
    if (!rate || rate.totalSupplyShares === 0n) continue
    const shares = positionMap.get(`${vaultAddress}:${id}`)
    if (!shares || shares === 0n) continue
    const allocAssetsRaw =
      (shares * rate.totalSupplyAssets) / rate.totalSupplyShares
    const allocAssets = Number(
      parseRawAmount(allocAssetsRaw.toString(), decimals),
    )
    if (allocAssets <= 0) continue
    weighted += allocAssets * rate.supplyApr
    allocated += allocAssets

    // The vault can pull at most the market's liquid cash from this slice.
    // Unknown liquidity (no borrow side read) ⇒ treat the slice as fully
    // liquid, matching the previous optimistic behaviour.
    const marketLiquidity =
      rate.liquidity != null
        ? Number(parseRawAmount(rate.liquidity.toString(), decimals))
        : allocAssets
    withdrawable += Math.min(allocAssets, Math.max(0, marketLiquidity))

    const marketUid = marketUidFor(uidCtx, id)
    exposures.push({
      marketId: id,
      ...(marketUid ? { marketUid } : {}),
      collateralAddress: rate.collateralAddress,
      collateral: tokenList[rate.collateralAddress],
      assets: allocAssets,
      assetsUsd: allocAssets * priceUsd,
      weightPct: (allocAssets / totalAssetsFormatted) * 100,
      supplyApr: rate.supplyApr,
    })
  }

  exposures.sort((a, b) => b.weightPct - a.weightPct)
  const grossApr = weighted / totalAssetsFormatted
  // Idle (unallocated) deposits are always instantly withdrawable.
  const idle = Math.max(0, totalAssetsFormatted - allocated)
  return {
    depositRate: grossApr * (1 - feePercent / 100),
    // Tag uninvested deposits as an idle entry so the breakdown sums to ~100%.
    exposures: withIdleExposure(exposures, totalAssetsFormatted, priceUsd),
    liquidityFormatted: Math.min(totalAssetsFormatted, idle + withdrawable),
  }
}

/**
 * Allocation walk for **Morpho Vaults V2** (adapter-based). Unlike V1, the
 * per-market assets come **directly** from the adapter's
 * `expectedSupplyAssets(marketId)` (no share→asset conversion), so this takes a
 * pre-resolved `marketAssetsRaw` map (marketId → raw underlying) instead of
 * `positionMap` + a share/asset ratio. Everything else mirrors
 * {@link computeVaultAllocation}: market supply APR + liquidity come from the
 * same lens-sourced `rateMap`, the result is allocation-weighted over total
 * assets and net of the vault's performance fee, and `liquidity` is
 * `idle + Σ min(allocation, market liquid)` clamped to total assets.
 */
export function computeV2Allocation(
  decimals: number,
  totalAssetsFormatted: number,
  feePercent: number,
  priceUsd: number,
  marketAssetsRaw: Map<string, bigint>,
  rateMap: Map<string, MarketRate>,
  tokenList: GenericTokenList,
  uidCtx?: MarketUidContext,
): {
  depositRate: number
  exposures: VaultMarketExposure[]
  liquidityFormatted: number
} {
  if (totalAssetsFormatted <= 0) {
    return { depositRate: 0, exposures: [], liquidityFormatted: 0 }
  }
  if (marketAssetsRaw.size === 0) {
    return {
      depositRate: 0,
      exposures: withIdleExposure([], totalAssetsFormatted, priceUsd),
      liquidityFormatted: totalAssetsFormatted,
    }
  }

  let weighted = 0
  let allocated = 0
  let withdrawable = 0
  const exposures: VaultMarketExposure[] = []
  for (const [id, assetsRaw] of marketAssetsRaw) {
    const allocAssets = Number(parseRawAmount(assetsRaw.toString(), decimals))
    if (allocAssets <= 0) continue
    const rate = rateMap.get(id)
    // Markets with no rate (lens miss) still count toward `allocated` so they
    // aren't double-counted as idle — they just contribute 0 APR and, lacking
    // a liquidity read, are treated as fully liquid.
    allocated += allocAssets
    const supplyApr = rate?.supplyApr ?? 0
    weighted += allocAssets * supplyApr
    const marketLiquidity =
      rate?.liquidity != null
        ? Number(parseRawAmount(rate.liquidity.toString(), decimals))
        : allocAssets
    withdrawable += Math.min(allocAssets, Math.max(0, marketLiquidity))

    const marketUid = marketUidFor(uidCtx, id)
    const collateralAddress = rate?.collateralAddress ?? ''
    exposures.push({
      marketId: id,
      ...(marketUid ? { marketUid } : {}),
      collateralAddress,
      collateral: collateralAddress ? tokenList[collateralAddress] : undefined,
      assets: allocAssets,
      assetsUsd: allocAssets * priceUsd,
      weightPct: (allocAssets / totalAssetsFormatted) * 100,
      supplyApr,
    })
  }

  exposures.sort((a, b) => b.weightPct - a.weightPct)
  const grossApr = weighted / totalAssetsFormatted
  const idle = Math.max(0, totalAssetsFormatted - allocated)
  return {
    depositRate: grossApr * (1 - feePercent / 100),
    exposures: withIdleExposure(exposures, totalAssetsFormatted, priceUsd),
    liquidityFormatted: Math.min(totalAssetsFormatted, idle + withdrawable),
  }
}
